import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useId, useRef, useState } from "react";
import { PlacesAutocomplete, TextInput } from "../../../components/ui/form";
import { useCountryDetection } from "../../../hooks/useCountryDetection";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers } from "../../../types/onboarding";
import styles from "./SetupProfileQuestion.module.css";

interface SetupProfileQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | number | string[]) => void;
	showValidation?: boolean;
}

export function SetupProfileQuestion({
	answers,
	onInputChange,
	showValidation = false,
}: SetupProfileQuestionProps) {
	const { t, direction } = useClientTranslations("onboarding");
	const { logOnboarding } = useAnalytics();
	const nameInputId = useId();
	const locationInputId = useId();
	const [showNameInfo, setShowNameInfo] = useState(false);
	const [showLocationInfo, setShowLocationInfo] = useState(false);
	// Local state to preserve full name input including trailing spaces
	const [localNameValue, setLocalNameValue] = useState("");
	// Track if we're currently in the middle of user input
	const isUserTyping = useRef(false);
	// Fire the field-entered events once per screen visit, not per keystroke.
	const nameTracked = useRef(false);
	const locationTracked = useRef(false);

	// Auto-detect user's location based on IP
	const {
		city,
		country,
		isLoading: isDetectingLocation,
	} = useCountryDetection();
	const hasAutoPopulated = useRef(false);

	// Initialize local state from answers ONLY on first render
	useEffect(() => {
		// Only sync if:
		// 1. We haven't started typing yet (!isUserTyping.current)
		// 2. localNameValue is empty
		// 3. We have data from backend
		if (!isUserTyping.current && !localNameValue) {
			const firstName = answers.firstName || "";
			const lastName = answers.lastName || "";
			if (firstName || lastName) {
				const fullName = lastName ? `${firstName} ${lastName}` : firstName;
				setLocalNameValue(fullName);
			}
		}
	}, [answers.firstName, answers.lastName, localNameValue]);

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		isUserTyping.current = true;

		// Update local state immediately to preserve spaces
		setLocalNameValue(value);

		// Split and update firstName/lastName
		const names = value.split(" ");
		const firstName = names[0] || "";
		const lastName = names.slice(1).join(" ");

		onInputChange("firstName", firstName);
		onInputChange("lastName", lastName);

		// Fire once when the name field first gets content (engagement signal),
		// rather than one event per keystroke.
		if (value.trim() && !nameTracked.current) {
			nameTracked.current = true;
			logOnboarding("profile_name_entered", { field_name: "firstName" });
		}
	};

	const handleLocationChange = (value: string) => {
		onInputChange("countryCity", value);
		if (value && !locationTracked.current) {
			locationTracked.current = true;
			logOnboarding("profile_location_selected", {
				field_name: "countryCity",
			});
		}
	};

	// Auto-populate location when detection completes and field is empty
	useEffect(() => {
		if (
			!isDetectingLocation &&
			!hasAutoPopulated.current &&
			!answers.countryCity &&
			(city || country)
		) {
			const detectedLocation =
				city && country ? `${city}, ${country}` : country;
			if (detectedLocation) {
				onInputChange("countryCity", detectedLocation);
				hasAutoPopulated.current = true;
			}
		}
	}, [isDetectingLocation, city, country, answers.countryCity, onInputChange]);

	// Check if fields have errors when validation is shown
	// Location is optional, so only name is required
	const nameHasError = showValidation && !localNameValue.trim();
	const locationHasError = false; // Location is optional

	return (
		<div className={styles.container} dir={direction}>
			<div className={styles.inputCard}>
				<div className={styles.inputGroup}>
					<div className={styles.labelRow}>
						<label
							htmlFor={nameInputId}
							className={`${styles.label} ${nameHasError ? styles.labelError : ""}`}
						>
							{t("setupProfile.nameQuestion")}
						</label>
						<button
							type="button"
							className={styles.infoButton}
							onClick={() => {
								const next = !showNameInfo;
								setShowNameInfo(next);
								logOnboarding("profile_name_info_toggled", { open: next });
							}}
							aria-label="Information about name"
						>
							<InformationCircleIcon className={styles.infoIcon} />
						</button>
					</div>
					<TextInput
						id={nameInputId}
						variant="onboarding"
						placeholder={t("setupProfile.namePlaceholder")}
						value={localNameValue}
						onChange={handleNameChange}
						className={`${styles.input} ${nameHasError ? styles.inputError : ""}`}
					/>
					{showNameInfo && (
						<div className={styles.infoTooltip}>
							{t("setupProfile.nameInfo")}
						</div>
					)}
				</div>

				<div className={styles.inputGroup}>
					<div className={styles.labelRow}>
						<label
							htmlFor={locationInputId}
							className={`${styles.label} ${locationHasError ? styles.labelError : ""}`}
						>
							{t("setupProfile.locationQuestion")}
						</label>
						<button
							type="button"
							className={styles.infoButton}
							onClick={() => {
								const next = !showLocationInfo;
								setShowLocationInfo(next);
								logOnboarding("profile_location_info_toggled", { open: next });
							}}
							aria-label="Information about location"
						>
							<InformationCircleIcon className={styles.infoIcon} />
						</button>
					</div>
					<PlacesAutocomplete
						id={locationInputId}
						variant="onboarding"
						placeholder={t("setupProfile.locationPlaceholder")}
						value={answers.countryCity || ""}
						onChange={handleLocationChange}
						className={`${styles.input} ${locationHasError ? styles.inputError : ""}`}
					/>
					{showLocationInfo && (
						<div className={styles.infoTooltip}>
							{t("setupProfile.locationInfo")}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
